import React, { useEffect, useState, lazy, Suspense } from "react";
import { v4 as uuidv4 } from "uuid";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import MobileStepper from "@mui/material/MobileStepper";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import useMediaQuery from "@mui/material/useMediaQuery";

import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import StopIcon from "@mui/icons-material/Stop";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";

import ExportFlightWaypointsMenu from "./exportFlightWaypointsDialog";
import { FlightHistoryDialog } from "./flightHistoryDialog";
import { FlightInfoDialog } from "./flightInfoDialog";
import FlightCreator from "./flightCreator";

import { GridActionsCellItem } from "@mui/x-data-grid";
import { CustomPagination } from "../../customPagination";
import { PieChart } from "@mui/x-charts/PieChart";
import { ConvertISOToDate, GetColorFromState, getOperationDateFromIsoString } from "../../util";
import { getOperationEditable, getOperationPriority, handleFlightStatusChange, handleFlightStatusChangePromise } from "./opUtil";

import { useUserAuth } from "../../contexts/authContext";
import { useMap } from "../../contexts/mapContext";
import { useEnv } from "../../contexts/envContext";

import { useNavigate } from "react-router";

import * as CONSTANT from "./opConstants";
import ResponsiveGrid from "../../grid/responsiveGrid";
import ConflictionMap from "../../map/mapConfliction";
import DfrPublish from "./dfrPublish";
import DfrPreview from "./dfrPreview";

const LAANCDetailsDialog = lazy(() => import("./laancDetailsDialog"));

const OperatorDashboard = () => {
    const [upcomingOperations, setUpcomingOperations] = useState([]);
    const [allOperations, setAllOperations] = useState([]);
    const [searchedOperations, setSearchedOperations] = useState([]);
    const [sortModel, setSortModel] = useState([{ field: "time_end", sort: "desc" }]);

    const [pieChartIndex, setPieChartIndex] = useState(0);
    const [pieCharts, setPieCharts] = useState([]);
    const [totalFlightTime, setTotalFlightTime] = useState("0hr 0m");
    const [totalDrones, setTotalDrones] = useState("0");

    const [conflictedFlight, setConflictedFlight] = useState(null);
    const [conflictionMapOpen, setConflictionMapOpen] = useState(false);

    const [downloadWaypointDialogOpen, setDownloadWaypointDialogOpen] = useState(false);
    const [downloadWaypointOperation, setDownloadWaypointOperation] = useState(null);

    const [selectedFlightHistoryFlight, setSelectedFlightHistoryFlight] = useState(null);
    const [flightHistoryDialogOpen, setFlightHistoryDialogOpen] = useState(false);

    const [infoDialogFlight, setInfoDialogFlight] = useState(null);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);

    const [flightDetailsVolume, setFlightDetailsVolume] = useState(null);
    const [flightDetailsDialogOpen, setFlightDetailsDialogOpen] = useState(false);

    const [editFlightDialogOpen, setEditFlightDialogOpen] = useState(false);
    const [editFlightVolume, setEditFlightVolume] = useState(null);

    const [dfrFlights, setDfrFlights] = useState([]);
    const [dfrPublishFlight, setDfrPublishFlight] = useState(null);
    const [dfrPreviewFlight, setDfrPreviewFlight] = useState(null);
    const [dfrPublishOpen, setDfrPublishOpen] = useState(false);
    const [dfrPreviewOpen, setDfrPreviewOpen] = useState(false);

    const { user, setUser, socket, handleFailedFetch, userOperationalStates } = useUserAuth();
    const { publishedOperations, drones, getDrones } = useMap();
    const { laancOn } = useEnv();

    const navigate = useNavigate();
    const isDesktop = useMediaQuery("(min-width:900px)");

    const columns = [
        {
            field: "info",
            headerName: "",
            minWidth: 50,
            maxWidth: 50,
            flex: 1,
            display: "flex",
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                    <Tooltip title="Information">
                        <IconButton
                            size="medium"
                            onClick={() => {
                                setInfoDialogFlight(row);
                                setInfoDialogOpen(true);
                            }}
                            id={`info-${row.volumeId}`}
                            sx={{ p: 0 }}
                        >
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "vehicle_uuid",
            headerName: "Vehicle",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                return getDroneFromVehicleUuid(value);
            }
        },
        {
            field: "time_start",
            headerName: "Start Date",
            minWidth: 200,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                return ConvertISOToDate(value);
            }
        },
        {
            field: "time_end",
            headerName: "End Date",
            minWidth: 200,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                return ConvertISOToDate(value);
            }
        },
        {
            field: "bvlos",
            headerName: "BVLOS",
            minWidth: 100,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                return value ? "BVLOS" : "VLOS";
            }
        },
        {
            field: "dfr",
            headerName: "DFR",
            headerAlign: "center",
            minWidth: 100,
            flex: 1,
            editable: false,
            renderCell: ({ row }) => {
                // Allow publish if the operation is in a final state (deleted or time expired).
                const hasEnded = new Date(row.time_end).getTime() <= new Date().getTime();
                const publishable = row.state === "DELETED" || hasEnded;
                if (!publishable) {
                    return <></>;
                }

                // Must be in flights table (not planning flights).
                if (row.original_table !== "flights") {
                    return <></>;
                }

                const dfrFlight = dfrFlights.find((dfr) => dfr.flight_uuid === row.flight_uuid);
                const published = dfrFlight && dfrFlight.published;
                const flight = {
                    flight_uuid: row.flight_uuid,
                    name: row.name,
                    incident: dfrFlight?.incident || "",
                    location: dfrFlight?.location || "",
                    summary: dfrFlight?.summary || "",
                    telemetry: dfrFlight?.telemetry,
                    volumes: row.volumes
                };

                return (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {published ? (
                            <Button
                                onClick={() => {
                                    setDfrPreviewFlight(flight);
                                    setDfrPreviewOpen(true);
                                }}
                            >
                                View
                            </Button>
                        ) : (
                            <Button
                                onClick={() => {
                                    setDfrPublishFlight(flight);
                                    setDfrPublishOpen(true);
                                }}
                            >
                                Publish
                            </Button>
                        )}
                    </Box>
                );
            }
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 100,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const arr = [
                    <Tooltip key={1} title="History">
                        <GridActionsCellItem
                            icon={<HistoryIcon />}
                            label="History"
                            onClick={() => {
                                setSelectedFlightHistoryFlight(row);
                                setFlightHistoryDialogOpen(true);
                            }}
                            id={`history-${row.volumeId}`}
                        />
                    </Tooltip>
                ];

                const hasEnded = new Date(row.time_end).getTime() <= new Date().getTime() ? true : false;
                // Allow permanent delete if the operation is in a final state (deleted or time expired).
                const deletable = row.state === "DELETED" || hasEnded ? true : false;

                if (deletable) {
                    arr.push(
                        <Tooltip key={2} title="Delete">
                            <GridActionsCellItem
                                icon={<DeleteIcon />}
                                label="Delete"
                                onClick={() => handleDeleteFlightClick(row)}
                                id={`perm-delete-${row.volumeId}`}
                            />
                        </Tooltip>
                    );
                }
                return arr;
            }
        }
    ];

    const fetchDFR = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/dfr/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setDfrFlights(data))
            .catch((err) => handleFailedFetch(err));
    };

    const fetchOperations = () => {
        if (user && user.pilot_uuid && Array.isArray(drones) && drones.length > 0) {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            fetch(`/api/op/getuser/${user.pilot_uuid}`, requestOptions)
                .then((res) => (res.ok ? res.json() : Promise.reject(res)))
                .then((data) => {
                    setAllOperations(setupVolumeIds(data));
                    setSearchedOperations(setupVolumeIds(data));

                    getTotalDrones(data);
                    getTotalFlightTime(data);
                    getPieChartData(data);
                })
                .catch((err) => handleFailedFetch(err));
        }
    };

    useEffect(() => {
        getDrones();
        fetchDFR();
    }, []);

    useEffect(() => {
        const operations = publishedOperations
            .filter((op) => op.pilot_uuid === user.pilot_uuid)
            .sort((a, b) => new Date(a.time_start).getTime() - new Date(b.time_start).getTime());
        setUpcomingOperations(operations);
    }, [publishedOperations]);

    useEffect(() => {
        fetchOperations();
    }, [drones]);

    const setupVolumeIds = (volumes) => {
        const volumeCount = {};
        return volumes.map((volume) => {
            const name = volume.name;
            const count = volumeCount[name] ? volumeCount[name] + 1 : 1;
            volumeCount[name] = count;
            return {
                ...volume,
                volumeId: `${name}-${count}`
            };
        });
    };

    const getDroneFromVehicleUuid = (vehicleUUID) => {
        const drone = drones.find((drone) => drone.vehicle_uuid === vehicleUUID);
        if (drone !== undefined) {
            return drone.name;
        } else {
            return "Unknown";
        }
    };
    const getTotalDrones = (flights) => {
        const totalVehicles = flights.reduce((acc, { vehicle_uuid }) => {
            if (acc.includes(vehicle_uuid) === false) {
                acc.push(vehicle_uuid);
            }
            return acc;
        }, []);
        setTotalDrones(totalVehicles.length);
    };
    const getTotalFlightTime = (flights) => {
        const totalDuration = flights.reduce((acc, { time_start, time_end }) => {
            const startTime = new Date(time_start).getTime();
            const endTime = new Date(time_end).getTime();
            const duration = endTime - startTime;

            acc += duration;
            return acc;
        }, 0);

        const hours = Math.floor(totalDuration / (1000 * 60 * 60));
        const minutes = Math.floor((totalDuration % (1000 * 60 * 60)) / (1000 * 60));
        setTotalFlightTime(`${hours}hrs ${minutes}m`);
    };
    const getPieChartData = (flights) => {
        const charts = [];
        const fields = [
            { title: "Flights Per Vehicle", field: "vehicle_uuid" },
            { title: "Flights Per Operation Type", field: "bvlos" },
            { title: "Flights Per Priority", field: "priority" }
        ];
        fields.forEach(({ title, field }) => {
            const arr = [];
            const mp = flights.reduce((acc, flight) => {
                let label = "";
                if (field === "vehicle_uuid") {
                    label = getDroneFromVehicleUuid(flight.vehicle_uuid);
                } else if (field === "bvlos") {
                    label = flight[field] ? "BVLOS" : "VLOS";
                } else {
                    label = getOperationPriority(flight);
                }
                const count = acc.get(label);
                if (count !== undefined) {
                    acc.set(label, count + 1);
                } else {
                    acc.set(label, 1);
                }
                return acc;
            }, new Map());

            mp.forEach((count, key) => {
                if (key.length > 15) {
                    key = key.substring(0, 15).trim() + "...";
                }
                arr.push({ id: key, label: key, value: count });
            });
            charts.push({
                title: title,
                series: [{ paddingAngle: 5, innerRadius: "65%", outerRadius: "80%", data: arr }]
            });
        });
        setPieCharts(charts);
    };
    // const handleSettingsClick = () => {
    //     console.log("this is for OIS and DAA configurations");
    // };
    const handleSearchValueChange = (e) => {
        const search = e.target.value;
        if (search !== undefined && search.length) {
            const operations = allOperations.filter((op) => {
                return op.name.toLowerCase().includes(search.toLowerCase());
            });
            setSearchedOperations(operations);
        } else {
            setSearchedOperations(allOperations);
        }
    };
    const handleRegisterPilotClick = () => {
        const registeredUser = {
            ...user,
            pilot_uuid: uuidv4()
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(registeredUser)
        };
        fetch("api/account/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                setUser(registeredUser);
                socket.emit("storeUserChatSocket", registeredUser);
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleDeleteFlightClick = (row) => {
        if (confirm("This will permanently delete this flight from your account and cannot be undone. Do you wish to continue?")) {
            const operation = row;
            if (operation.flight_json && operation.flight_json.laanc && operation.flight_json.laanc.volumes) {
                operation.flight_json.laanc.volumes.forEach((vol) => {
                    vol.state = CONSTANT.LAANC_DELETED;
                });
            }
            const requestOptions = {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(operation)
            };
            fetch("/api/op/perm/delete", requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then(() => {
                    const operations = allOperations.filter((op) => op.flight_uuid !== operation.flight_uuid);
                    setAllOperations(operations);
                    getTotalDrones(operations);
                    getTotalFlightTime(operations);
                    getPieChartData(operations);
                    setSearchedOperations(searchedOperations.filter((op) => op.flight_uuid !== operation.flight_uuid));
                })
                .catch((err) => handleFailedFetch(err));

            // Delete from flight_dfr table as well.
            const requestOptions2 = {
                method: "PUT",
                headers: { "Content-Type": "application/json" }
            };
            fetch(`/api/dfr/delete/${operation.flight_uuid}`, requestOptions2)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then(() => fetchDFR())
                .catch((err) => handleFailedFetch(err));
        }
    };
    const handleDetailsButtonClicked = (operation) => {
        setFlightDetailsDialogOpen(true);
        setFlightDetailsVolume(operation);
    };
    const handleEditFlightButtonClick = (operation) => {
        setEditFlightDialogOpen(true);
        setEditFlightVolume(operation);
    };
    return editFlightDialogOpen ? (
        <FlightCreator
            setFlightCreatorOpen={setEditFlightDialogOpen}
            flightUUID={editFlightVolume.flight_uuid}
            publishedFlight={true}
            flightState={editFlightVolume.state}
        />
    ) : (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, ml: "unset" }}>
            <Grid container spacing={2}>
                {/* welcome */}
                <Grid size={12}>
                    <Paper
                        sx={{
                            p: 4,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            gap: 2
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "row", sm: "column" },
                                gap: 1,
                                justifyContent: { xs: "space-between", sm: "unset" },
                                width: { xs: "100%", sm: "unset" }
                            }}
                        >
                            <Typography variant="h5" gutterBottom id="hello">
                                Hello{user.first_name ? ` ${user.first_name}` : ""}
                            </Typography>

                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => navigate("/account")} id="viewProfile">
                                    View Profile
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 3 }}>
                            {[
                                { title: "Flights", value: allOperations.length, tooltip: "Total amount of flights created" },
                                { title: "Drones", value: totalDrones, tooltip: "Total amount of drones flown" },
                                { title: "Flight Time", value: totalFlightTime, tooltip: "Total time ellapsed from each operations start to finish" }
                            ].map(({ title, value, tooltip }, i) => (
                                <Tooltip key={i} title={tooltip}>
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Typography variant="body1" gutterBottom>
                                            {title}
                                        </Typography>
                                        <Typography variant="h6" gutterBottom sx={{ textAlign: "center", whiteSpace: "nowrap" }} id={title}>
                                            {value}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {/* active user flights */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper
                        sx={{ p: 2, height: { xs: "unset", sm: 305 }, display: "flex", flexDirection: "column", alignItems: "flex-start", overflowY: "auto" }}
                    >
                        <Typography variant="h6">Upcoming Flights</Typography>

                        <Box sx={{ width: "100%" }}>
                            {Array.isArray(upcomingOperations) && upcomingOperations.length > 0 ? (
                                <List dense={true} sx={{ m: 0, p: 0 }}>
                                    {upcomingOperations.map((operation, i) => {
                                        const isPublishedOperation = publishedOperations.find((op) => op.flight_uuid === operation.flight_uuid);
                                        let laancBadgeInvisible = true;
                                        if (operation.laanc && operation.laanc.state) {
                                            const laancState = operation.laanc.state;
                                            if (laancState === CONSTANT.LAANC_RESCINDED_AWAITING || laancState === CONSTANT.LAANC_INVALID_AWAITING) {
                                                laancBadgeInvisible = false;
                                            }
                                        }

                                        const editable = getOperationEditable(operation, user);
                                        return (
                                            <ListItem key={i}>
                                                <ListItemText
                                                    primary={
                                                        <>
                                                            {operation.name}
                                                            {isDesktop ? (
                                                                <Typography
                                                                    component="span"
                                                                    variant="body2"
                                                                    sx={{
                                                                        display: "inline",
                                                                        color: GetColorFromState(operation.state, userOperationalStates)
                                                                    }}
                                                                    id={`state-${operation.volumeId}`}
                                                                >
                                                                    {` - ${operation.state}`}
                                                                </Typography>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            <Typography component="span" variant="body2" sx={{ display: "inline", color: "#6b778c" }}>
                                                                {` - ${getOperationDateFromIsoString(operation.time_start)}`}
                                                            </Typography>
                                                        </>
                                                    }
                                                    secondary={
                                                        <>
                                                            {!isDesktop ? (
                                                                <Typography
                                                                    component="span"
                                                                    variant="body2"
                                                                    sx={{
                                                                        display: "inline",
                                                                        color: GetColorFromState(operation.state, userOperationalStates)
                                                                    }}
                                                                >
                                                                    {operation.state}
                                                                </Typography>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {operation.laanc && operation.laanc.state ? (
                                                                <Typography component="span" variant="body2" sx={{ display: "inline" }}>
                                                                    {`${!isDesktop ? " - " : ""}${operation.laanc.state}`}
                                                                </Typography>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {operation.conflicted && (
                                                                <>
                                                                    <Typography component="span" variant="body2" sx={{ display: "inline", color: "#f44336" }}>
                                                                        {!isDesktop || (operation.laanc && operation.laanc.state) ? " - " : ""}
                                                                    </Typography>
                                                                    <Button
                                                                        onClick={() => {
                                                                            setConflictedFlight(operation);
                                                                            setConflictionMapOpen(true);
                                                                        }}
                                                                        variant="body2"
                                                                        sx={{ p: 0, color: "#f44336", "&:hover": { textDecoration: "underline" } }}
                                                                    >
                                                                        CONFLICTED
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </>
                                                    }
                                                />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    {isPublishedOperation !== undefined ? (
                                                        !operation.conflicted && operation.state === "ACCEPTED" ? (
                                                            <Tooltip title="Activate">
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="start"
                                                                    onClick={() => handleFlightStatusChange(operation, "ACTIVATED", user, handleFailedFetch)}
                                                                    id={`activate-${operation.volumeId}`}
                                                                >
                                                                    <PlayArrowIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="End">
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="end"
                                                                    onClick={() => handleFlightStatusChange(operation, "ENDED", user, handleFailedFetch)}
                                                                    id={`end-${operation.volumeId}`}
                                                                >
                                                                    <StopIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {laancOn && operation.faa_approval_required ? (
                                                        <Tooltip title="LAANC">
                                                            <Badge badgeContent={"!"} color={"error"} invisible={laancBadgeInvisible}>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="LAANC"
                                                                    onClick={() => handleDetailsButtonClicked(operation)}
                                                                >
                                                                    <ArticleIcon />
                                                                </IconButton>
                                                            </Badge>
                                                        </Tooltip>
                                                    ) : editable ? (
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                aria-label="settings"
                                                                onClick={() => handleEditFlightButtonClick(operation)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {operation.waypoints && operation.waypoints.length > 0 ? (
                                                        <Tooltip title="Download">
                                                            <IconButton
                                                                size="small"
                                                                aria-label="start"
                                                                onClick={() => {
                                                                    setDownloadWaypointDialogOpen(true);
                                                                    setDownloadWaypointOperation(operation);
                                                                }}
                                                                id={`download-${operation.volumeId}`}
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="medium"
                                                            aria-label="delete"
                                                            onClick={() => {
                                                                handleFlightStatusChangePromise(operation, "DELETED", user, handleFailedFetch).then(() =>
                                                                    fetchOperations()
                                                                );
                                                            }}
                                                            id={`delete-${operation.volumeId}`}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            ) : (
                                <Box sx={{ mt: 2 }}>
                                    <Divider>
                                        <TaskAltIcon fontSize="large" />
                                    </Divider>
                                    <Typography variant="h5" sx={{ my: 2, textAlign: "center" }} id="allSet">
                                        {"You're all set"}
                                    </Typography>
                                    <Typography variant="body1" sx={{ my: 2, textAlign: "center" }}>
                                        {"You don't have any published operations."}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* scrollable pie charts */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper sx={{ p: 2, height: 305, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        {Array.isArray(pieCharts) && pieCharts.length > 0 ? (
                            <>
                                <Typography variant="h6">{pieCharts[pieChartIndex] !== undefined ? pieCharts[pieChartIndex].title : ""}</Typography>
                                {pieCharts.map(({ series }, i) => {
                                    return i === pieChartIndex ? (
                                        series && series[0] && Array.isArray(series[0].data) && series[0].data.length > 0 ? (
                                            <PieChart
                                                key={i}
                                                series={series}
                                                slotProps={{
                                                    legend: {
                                                        direction: "vertical",
                                                        position: {
                                                            vertical: "middle",
                                                            horizontal: "right"
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <PieChart
                                                key={i}
                                                series={[
                                                    {
                                                        paddingAngle: 5,
                                                        innerRadius: "85%",
                                                        outerRadius: "100%",
                                                        data: [{ label: "N/A", value: 1 }]
                                                    }
                                                ]}
                                            />
                                        )
                                    ) : (
                                        <React.Fragment key={i}></React.Fragment>
                                    );
                                })}
                                <MobileStepper
                                    variant="dots"
                                    steps={pieCharts.length}
                                    position="static"
                                    activeStep={pieChartIndex}
                                    sx={{ p: 0, backgroundColor: "transparent" }}
                                    nextButton={
                                        <IconButton
                                            size="small"
                                            onClick={() => setPieChartIndex((prev) => prev + 1)}
                                            disabled={pieChartIndex === pieCharts.length - 1}
                                        >
                                            <KeyboardArrowRight />
                                        </IconButton>
                                    }
                                    backButton={
                                        <IconButton size="small" onClick={() => setPieChartIndex((prev) => prev - 1)} disabled={pieChartIndex === 0}>
                                            <KeyboardArrowLeft />
                                        </IconButton>
                                    }
                                />
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" id="analytics">
                                    Analytics
                                </Typography>
                                <PieChart
                                    series={[
                                        {
                                            paddingAngle: 5,
                                            innerRadius: "65%",
                                            outerRadius: "80%",
                                            data: [{ label: "N/A", value: 1 }]
                                        }
                                    ]}
                                />
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* flight history */}
                <Grid size={12}>
                    <Paper sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1 }}>
                            <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>
                                All Flights
                            </Typography>
                            <TextField
                                label="Search by name"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                onChange={handleSearchValueChange}
                                size="small"
                                type="text"
                            />
                        </Box>
                        {user.pilot_uuid ? (
                            <Box sx={{ height: 225, width: "100%" }}>
                                <ResponsiveGrid
                                    primaryColumns={["name", "time_start"]}
                                    rows={searchedOperations}
                                    columns={columns}
                                    disableRowSelectionOnClick
                                    getRowId={(row) => row.flight_uuid}
                                    pagination
                                    slots={{ pagination: CustomPagination }}
                                    hideFooter={true}
                                    sortModel={sortModel}
                                    onSortModelChange={(model) => setSortModel(model)}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2, width: "100%" }}>
                                <Typography variant="body1">
                                    {"It appears you have not yet registered as a pilot. If you would like to do so, click below."}
                                </Typography>
                                <Box>
                                    <Button onClick={handleRegisterPilotClick} variant="contained" id="register">
                                        Register
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            {/* confliction map dialog */}
            {conflictionMapOpen && (
                <ConflictionMap conflictedFlight={conflictedFlight} conflictionMapOpen={conflictionMapOpen} setConflictionMapOpen={setConflictionMapOpen} />
            )}
            {/* download waypoint dialog */}
            {downloadWaypointDialogOpen ? (
                <ExportFlightWaypointsMenu
                    downloadWaypointDialogOpen={downloadWaypointDialogOpen}
                    setDownloadWaypointDialogOpen={setDownloadWaypointDialogOpen}
                    downloadWaypointOperation={downloadWaypointOperation}
                    setDownloadWaypointOperation={setDownloadWaypointOperation}
                />
            ) : (
                <></>
            )}
            {/* DFR Publish Dialog */}
            {dfrPublishOpen && <DfrPublish flight={dfrPublishFlight} setDfrPublishOpen={setDfrPublishOpen} fetchDFR={fetchDFR} />}
            {/* DFR Preview Dialog */}
            {dfrPreviewOpen && <DfrPreview dfr={dfrPreviewFlight} setDfrPreviewOpen={setDfrPreviewOpen} fetchDFR={fetchDFR} />}
            {/* flight history dialog */}
            {flightHistoryDialogOpen ? (
                <FlightHistoryDialog
                    flightUUID={selectedFlightHistoryFlight.flight_uuid}
                    flightHistoryOpen={flightHistoryDialogOpen}
                    setFlightHistoryOpen={setFlightHistoryDialogOpen}
                />
            ) : (
                <></>
            )}
            {/* info dialog */}
            {infoDialogOpen && infoDialogFlight ? (
                <FlightInfoDialog flight={infoDialogFlight} flightInfoOpen={infoDialogOpen} setFlightInfoOpen={setInfoDialogOpen} />
            ) : (
                <></>
            )}
            {/* laanc dialog */}
            {flightDetailsDialogOpen ? (
                <Suspense fallback={<></>}>
                    <LAANCDetailsDialog
                        flightDetailsVolume={flightDetailsVolume}
                        flightDetailsDialogOpen={flightDetailsDialogOpen}
                        publishedOperations={publishedOperations}
                        setFlightDetailsVolume={setFlightDetailsVolume}
                        setFlightDetailsDialogOpen={setFlightDetailsDialogOpen}
                    />
                </Suspense>
            ) : (
                <></>
            )}
        </Container>
    );
};

export default OperatorDashboard;
